import { ReactNode } from "react";
import {
  ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseFormStateReturn,
  type UseFormReturn,
} from "react-hook-form";

import { Side } from "../ui/CustomChervonIcon";

import {
  DEFAULT_MAX_STR_CHARS,
  DEFAULT_MAX_VALUE,
} from "@/app/constants/constants";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, InputProps } from "@/components/ui/input";
import { cn } from "@/utils/core/generalUtils";
import { InputKeyLimiter, InputPasteLimiter } from "@/utils/form/formHelper";

type FormInputProps<T extends FieldValues, TName extends Path<T>> = {
  form: UseFormReturn<T>;
  name: TName;
  label?: string;
  required?: boolean;
  description?: string | ReactNode;
  htmlFor?: string;
  horizontal?: boolean;
  input?: Omit<InputProps, keyof ControllerRenderProps<FieldValues, string>> & {
    disabled?: boolean;
    allowNegativeNumber?: boolean;
    allowDecimal?: boolean; // For number inputs only
    maxDecimalPlace?: number; // For number inputs only
    value?: string | number; // For controlled inputs
    defaultValue?: string | number;
    hideZero?: boolean;
  };
  className?: string;
  charLimit?: number;
  maxValue?: number; // For number inputs only
  render?: (
    field: ControllerRenderProps<T, TName>,
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<T>,
  ) => React.ReactNode;
  hideError?: boolean;
  warningMsg?: {
    node: ReactNode;
    position: Side;
  };
  formErrorClassName?: string;
};

/**
 *
 * @param {TProps<T>} props - FormInput props
 * @returns {React.ReactNode} - FormInput component
 */
export default function FormInput<T extends FieldValues, TName extends Path<T>>(
  props: FormInputProps<T, TName>,
): React.ReactNode {
  const {
    form,
    name,
    label,
    input,
    horizontal,
    htmlFor,
    required,
    description,
    render,
    className,
    charLimit,
    maxValue,
    hideError = false,
    warningMsg,
  } = props;

  // Destructure conflicting props from inputProps
  const {
    allowNegativeNumber,
    allowDecimal,
    maxDecimalPlace,
    hideZero,
    ...inputProps
  } = input ?? {};

  return (
    <FormField<T, TName>
      control={form.control}
      name={name}
      render={({ field, fieldState, formState }) => (
        <FormItem
          className={cn(
            className,
            horizontal
              ? "flex-row items-center justify-between space-y-0"
              : "3xl:space-y-3 space-y-2",
          )}
        >
          {label && (
            <div className='flex items-center justify-between'>
              <FormLabel htmlFor={htmlFor} className='font-normal w-fit'>
                {label}{" "}
                {required && (
                  <span className='text-red-500 text-xs leading-[8px]'> *</span>
                )}
              </FormLabel>
              {warningMsg && warningMsg.position === "top" && (
                <div className='mr-12'>{warningMsg.node}</div>
              )}
            </div>
          )}
          <FormControl>
            {render ? (
              render(field, fieldState, formState)
            ) : (
              <Input
                {...inputProps}
                {...field}
                value={hideZero && field.value === 0 ? "" : field.value}
                pattern={input?.type === "number" ? "d*" : ".*"}
                {...(input?.type === "number" && {
                  onWheel: (e): void => {
                    e.currentTarget.blur();
                  },
                })}
                onKeyDown={(e) =>
                  InputKeyLimiter({
                    event: e,
                    maxChars: charLimit ?? DEFAULT_MAX_STR_CHARS,
                    ...(input?.type === "number" && {
                      maxValue: maxValue ?? DEFAULT_MAX_VALUE,
                      allowNegative: allowNegativeNumber ?? false,
                      allowDecimal: allowDecimal ?? true,
                      maxDecimalPlace,
                    }),
                  })
                }
                onPaste={(e) =>
                  InputPasteLimiter({
                    event: e,
                    maxChars: DEFAULT_MAX_STR_CHARS,
                    ...(input?.type === "number" && {
                      maxValue: maxValue ?? DEFAULT_MAX_VALUE,
                      allowNegative: allowNegativeNumber ?? false,
                      allowDecimal: allowDecimal ?? true,
                      maxDecimalPlace,
                    }),
                  })
                }
              />
            )}
          </FormControl>
          {description && (
            <FormDescription className='text-xs'>{description}</FormDescription>
          )}
          {!hideError && <FormMessage className={props.formErrorClassName} />}
          {warningMsg && warningMsg.position === "bottom" && warningMsg.node}
        </FormItem>
      )}
    />
  );
}
